import { IntlShape } from 'react-intl';
import { ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';
import { formatCatalogDay } from './catalogDates';

/** What the portal half of a connection card has to say. */
export enum ArtistPortalCardState {
  /** No provider behind this service, or nothing signed in yet. */
  none = 'none',
  /** Signed in, one artist picked, numbers are coming in. */
  connected = 'connected',
  /** Signed in, but the login holds several artists and none is picked. */
  needsArtist = 'needsArtist',
  /** The stored session stopped working. */
  expired = 'expired',
}

export function artistPortalCardState(connection: ServiceConnection): ArtistPortalCardState {
  if (!connection.connectAvailable || connection.authKind === null) {
    return ArtistPortalCardState.none;
  }
  if (connection.status === ServiceConnectionStatus.expired) {
    return ArtistPortalCardState.expired;
  }
  if (connection.status !== ServiceConnectionStatus.connected) {
    return ArtistPortalCardState.none;
  }
  return connection.artist === null ? ArtistPortalCardState.needsArtist : ArtistPortalCardState.connected;
}

/**
 * Whether this service really answers with the artist's own numbers.
 *
 * A card can say `connected` while the artist has still to pick which artist
 * the login means, and nothing is read until they do. The strip counts only
 * the services that read.
 */
export function isArtistPortalReachable(connection: ServiceConnection): boolean {
  return artistPortalCardState(connection) === ArtistPortalCardState.connected;
}

const MS_PER_MINUTE = 60000;

/**
 * "Synced 2 hours ago · data through 11 Sep 2026".
 *
 * Takes only the two fields it reads, so the Streaming screen can call it with
 * an overview row rather than a connection row.
 */
export function formatPortalFreshness(
  intl: IntlShape,
  connection: { lastSyncAt: string | null; syncedThrough: string | null },
  now: number
): string | undefined {
  const { lastSyncAt, syncedThrough } = connection;
  if (lastSyncAt === null) {
    return undefined;
  }
  const minutes = Math.max(0, Math.round((now - Date.parse(lastSyncAt)) / MS_PER_MINUTE));
  const age =
    minutes < 60
      ? intl.formatRelativeTime(-minutes, 'minute', { numeric: 'auto' })
      : intl.formatRelativeTime(-Math.round(minutes / 60), 'hour', { numeric: 'auto' });
  const synced = intl.formatMessage(
    { id: 'app.sml.catalog.connection.synced', defaultMessage: 'Synced {age}' },
    { age }
  );
  if (syncedThrough === null) {
    return synced;
  }
  return intl.formatMessage(
    { id: 'app.sml.catalog.connection.synced_through', defaultMessage: '{synced} · data through {day}' },
    { synced, day: formatCatalogDay(intl, syncedThrough) }
  );
}
